import { useFormHelper } from "@plura/client/utils";
import { dynamicQuestionContent, getCurrentResponseScore } from "@plura/shared/lead-forms";
import { FormJourneyItem, LeadForm } from "@plura/shared/types";
import { cn } from "@plura/shared/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import SearchableDropdown from './SearchableDropdown';

interface Props {
  id: string;
  step: FormJourneyItem
  leadForm: LeadForm;
  editorMode: boolean;
  previewMode: boolean;
  active: boolean;
  handleAnswer?: () => void;
  onContentClick?: (fieldName: string, blockId: string) => void;
  hiddenFields?: {
    field: string;
    value: string;
  }[];
}

const DropdownScreen = ({ id, step, editorMode, previewMode, leadForm, active, handleAnswer, onContentClick, hiddenFields }: Props) => {
  const dropdownRef = useRef<{ focus: () => void } | null>(null);
  const form = useFormContext();
  const selectedOptions = form.getValues(step.blockId) ? [form.getValues(step.blockId)] : []
  const { onValueChange, errorMessage, ...formHelperProps } = useFormHelper({
    id: step.blockId,
  });
  const { conversationId, ...userResponses } = form.getValues()
  const currentResponseScore = userResponses
    ? getCurrentResponseScore(userResponses, leadForm)
    : 0

  useEffect(() => {
    // Inside a MULTI_QUESTION_PAGE we never auto-open the dropdown — it would
    // visually cover the sibling questions on the same page, which is the
    // opposite of what a stacked-form layout wants.
    if (step.parentId) return;
    if (active && dropdownRef.current && (!editorMode || (editorMode && previewMode))) {
      setTimeout(() => {
        dropdownRef.current?.focus();
      }, leadForm.transitionSpeed ?? 500);
    }
  }, [active]);

  const handleContentClick = (field: 'content' | 'secondaryContent') => {
    if ((!editorMode || (editorMode && previewMode)) || !onContentClick) return
    onContentClick(field, id)
  }

  return (
    <>
      <div className={cn('questionInner', 'dropdownItem', 'questionInner')}>
        <div>
          <h4
            onClick={() => handleContentClick('content')}
            className={
              cn(
                selectedOptions.length && 'semiFade',
                'question',
              )}
          >
            <motion.span
              dangerouslySetInnerHTML={{
                __html: dynamicQuestionContent({
                  content: step.content,
                  score: currentResponseScore,
                  editMode: editorMode,
                  previewMode: previewMode ?? false,
                  hiddenFields,
                  journeyItems: leadForm.journeyItems ?? [],
                  userResponses
                })
              }}
            />
          </h4>
          {
            step.secondaryContent &&
            <p
              onClick={() => handleContentClick('secondaryContent')}
              className={
                cn(
                  selectedOptions.length && 'semiFade',
                  'caption',
                )}
            >
              <motion.span
                dangerouslySetInnerHTML={{
                  __html: dynamicQuestionContent({
                    content: step.secondaryContent,
                    score: currentResponseScore,
                    editMode: editorMode,
                    previewMode: previewMode ?? false,
                    hiddenFields,
                    journeyItems: leadForm.journeyItems ?? [],
                    userResponses
                  })
                }}
              />
            </p>
          }
        </div>
        <div>
          <motion.div className={'answerInputWrapper'}>
            <AnimatePresence initial={false}>
              <motion.div className={'fieldWrapper'}>
                <SearchableDropdown
                  ref={dropdownRef}
                  id={step.blockId}
                  className={'searchableDropdown'}
                  placeholder="Type or select an option"
                  isClearable={true}
                  elementType={leadForm.elementType}
                  value={typeof formHelperProps.value === 'string' ? formHelperProps.value : undefined}
                  onChange={(e) => {
                    formHelperProps.onChange?.(e as React.ChangeEvent<HTMLInputElement>);
                    if (e.currentTarget.value) {
                      setTimeout(() => handleAnswer?.(), 450);
                    }
                  }}
                  options={step.selectOptions ?? []}
                />
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </>
  );
};


export default DropdownScreen

